import type { StateCreator } from 'zustand/vanilla';

import type { Locales } from '@/locales/resources';

import type { UserStore } from '../../store';
import type { UserSettings } from './initial-state';

export interface UserSettingsAction {
  /**
   * Update user settings
   */
  updateSettings: (settings: Partial<UserSettings>) => void;
  /**
   * Get settings from localStorage or state
   */
  getSettings: () => UserSettings;
  /**
   * Set settings loading state
   */
  setSettingsLoading: (isLoading: boolean) => void;
  /**
   * Set settings error
   */
  setSettingsError: (error?: string) => void;
  /**
   * Reset settings to default
   */
  resetSettings: () => void;
  /**
   * Update language preference
   */
  setLanguage: (language: Locales | 'auto') => void;
  /**
   * Update theme preference
   */
  setTheme: (theme: 'light' | 'dark' | 'auto') => void;
  /**
   * Update color preference
   */
  setColor: (color: string) => void;
}

export const createSettingsSlice: StateCreator<
  UserStore,
  [['zustand/persist', unknown], ['zustand/devtools', never]],
  [],
  UserSettingsAction
> = (set, get) => ({
  updateSettings: (newSettings: Partial<UserSettings>) => {
    const currentSettings = get().settings;
    const updatedSettings = { ...currentSettings, ...newSettings };

    // Persist middleware will automatically save to localStorage
    set({
      settings: updatedSettings,
      isUpdatingSettings: false,
      settingsError: undefined,
    });
  },

  getSettings: () => {
    const stateSettings = get().settings;
    return stateSettings;
  },

  setSettingsLoading: (isUpdatingSettings: boolean) => {
    set({ isUpdatingSettings });
  },

  setSettingsError: (settingsError?: string) => {
    set({ settingsError, isUpdatingSettings: false });
  },

  resetSettings: () => {
    const defaultSettings: UserSettings = {
      language: 'auto',
      theme: 'auto',
      color: '#966c6c',
    };

    // Persist middleware will automatically save to localStorage
    set({
      settings: defaultSettings,
      isUpdatingSettings: false,
      settingsError: undefined,
    });
  },

  setLanguage: (language: Locales | 'auto') => {
    const currentSettings = get().settings;
    const updatedSettings = { ...currentSettings, language };

    // Persist middleware will automatically save to localStorage
    set({ settings: updatedSettings });
  },

  setTheme: (theme: 'light' | 'dark' | 'auto') => {
    const currentSettings = get().settings;
    const updatedSettings = { ...currentSettings, theme };

    // Persist middleware will automatically save to localStorage
    set({ settings: updatedSettings });
  },

  setColor: (color: string) => {
    const currentSettings = get().settings;
    const updatedSettings = { ...currentSettings, color };

    // Persist middleware will automatically save to localStorage
    set({ settings: updatedSettings });
  },
});
